import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import {
  RotateCw,
  PieChart,
  List,
  CalendarDays,
  ChartNoAxesColumnIncreasing,
  Settings as SettingsIcon,
  Plus,
  Search,
  Bell,
  Moon,
  Sun,
  ChevronRight,
  ArrowUpRight,
  ArrowRight,
  Server,
  Globe2,
  Layers3,
  SlidersHorizontal,
  LayoutGrid,
  Rows3,
  Download,
  ShieldCheck,
  Smartphone,
  CircleHelp,
  AlertCircle,
  Check,
  X,
  RefreshCw,
  Wallet,
  CreditCard,
  TrendingUp,
  KeyRound,
  LogOut,
  ChevronDown,
  PanelLeftClose,
  PanelLeftOpen,
  Eye,
  EyeOff,
} from 'lucide-react';
import {
  categories,
  cycles,
  attention,
  calendarEvents,
  addDays,
  addMonths,
  monthlyCost,
  money,
  fullDate,
  relevantDate,
  shortDate,
  type Subscription,
  type AppData,
  type AccessInfo,
  type Category,
} from './lib/domain.ts';
import {
  Modal,
  EmptyState,
  SubscriptionCard,
  ServiceIcon,
  StatusBadge,
  UpcomingList,
} from './components/UI';
import {
  SubscriptionForm,
  PaymentForm,
  DetailModal,
  AccessModal,
  ConfirmModal,
  type Mutate,
} from './components/Forms';
import { CalendarPage, AnalyticsPage, SettingsPage, CostBreakdown } from './components/Pages';

type View = 'overview' | 'subscriptions' | 'calendar' | 'analytics' | 'settings';
type DialogState =
  | { type: 'create' | 'access' | 'reminders' }
  | { type: 'detail' | 'edit' | 'pay'; id: string }
  | null;
type Confirmation = { title: string; text: string; action: () => Promise<void> } | null;
const navigation = [
  { id: 'overview' as View, label: '总览', icon: PieChart },
  { id: 'subscriptions' as View, label: '订阅', icon: List },
  { id: 'calendar' as View, label: '日历', icon: CalendarDays },
  { id: 'analytics' as View, label: '分析', icon: ChartNoAxesColumnIncreasing },
  { id: 'settings' as View, label: '设置', icon: SettingsIcon },
];
async function api<T>(path: string, method = 'GET', body?: unknown): Promise<T> {
  const response = await fetch(`/api${path}`, {
    method,
    signal: AbortSignal.timeout(20000),
    headers: body === undefined ? {} : { 'Content-Type': 'application/json' },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
  const payload = await response.json().catch(() => ({ error: '连接暂时中断，请稍后重试' }));
  if (!response.ok)
    throw Object.assign(new Error(payload.error || '请求失败，请重试'), {
      status: response.status,
    });
  return payload as T;
}
function initialView(): View {
  const view = location.hash.slice(1);
  return navigation.some((n) => n.id === view) ? (view as View) : 'overview';
}

export default function App() {
  const [data, setData] = useState<AppData | null>(null);
  const [access, setAccess] = useState<AccessInfo | null>(null);
  const [loadError, setLoadError] = useState('');
  const [view, setCurrentView] = useState<View>(initialView);
  const [dialog, setDialog] = useState<DialogState>(null);
  const [confirmation, setConfirmation] = useState<Confirmation>(null);
  const [toast, setToast] = useState('');
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState<Category | 'all'>('all');
  const [kind, setKind] = useState<'recurring' | 'once'>('recurring');
  const [status, setStatus] = useState<'active' | 'paused' | 'all'>('active');
  const [renew, setRenew] = useState<'all' | 'auto' | 'manual'>('all');
  const [sort, setSort] = useState('date');
  const [layout, setLayout] = useState<'cards' | 'table'>(() =>
    localStorage.getItem('renew-layout') === 'table' ? 'table' : 'cards',
  );
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    () => localStorage.getItem('renew-sidebar-collapsed') === 'true',
  );
  const [showFilters, setShowFilters] = useState(false);
  const [theme, setTheme] = useState(() => localStorage.getItem('renew-theme') || 'light');
  const [lastSync, setLastSync] = useState(Date.now());
  const [syncing, setSyncing] = useState(false);
  const syncSequence = useRef(0);
  const searchRef = useRef<HTMLInputElement>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const notify = useCallback((message: string) => {
    setToast(message);
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(''), 4200);
  }, []);
  function setView(next: View) {
    location.hash = next;
    setCurrentView(next);
  }
  useEffect(() => {
    const handler = () => setCurrentView(initialView());
    window.addEventListener('hashchange', handler);
    return () => window.removeEventListener('hashchange', handler);
  }, []);
  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    localStorage.setItem('renew-theme', theme);
  }, [theme]);
  useEffect(() => {
    localStorage.setItem('renew-layout', layout);
  }, [layout]);
  useEffect(() => {
    localStorage.setItem('renew-sidebar-collapsed', String(sidebarCollapsed));
  }, [sidebarCollapsed]);
  const refresh = useCallback(async () => {
    const sequence = ++syncSequence.current;
    setSyncing(true);
    try {
      const loaded = await api<AppData>('/data');
      if (sequence !== syncSequence.current) return;
      setAccess(loaded.access);
      setData(loaded);
      setLastSync(Date.now());
      setLoadError('');
    } catch (error) {
      if (sequence !== syncSequence.current) return;
      if ((error as { status?: number }).status === 401) {
        setData(null);
        setDialog(null);
        setConfirmation(null);
        try {
          setAccess(await api<AccessInfo>('/access'));
          setLoadError('');
        } catch {
          setLoadError('无法连接工作空间，请检查网络后重试');
        }
      } else
        setLoadError(
          (error as Error).name === 'TimeoutError' || (error as Error).name === 'TypeError'
            ? '无法连接工作空间，请检查网络后重试'
            : (error as Error).message,
        );
    } finally {
      if (sequence === syncSequence.current) setSyncing(false);
    }
  }, []);
  useEffect(() => {
    void refresh();
    const timer = setInterval(() => {
      if (!document.hidden) void refresh();
    }, 20000);
    const onFocus = () => {
      if (!document.hidden) void refresh();
    };
    document.addEventListener('visibilitychange', onFocus);
    window.addEventListener('focus', onFocus);
    window.addEventListener('online', onFocus);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onFocus);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('online', onFocus);
    };
  }, [refresh]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  const mutate: Mutate = async (path, method, body, message) => {
    await api(path, method, body);
    await refresh();
    if (message) notify(message);
  };
  function confirm(title: string, text: string, action: () => Promise<void>) {
    setConfirmation({ title, text, action });
  }
  const open = (sub: Subscription) => setDialog({ type: 'detail', id: sub.id });
  const pay = (sub: Subscription) => setDialog({ type: 'pay', id: sub.id });
  const selected =
    dialog && 'id' in dialog ? data?.subscriptions.find((s) => s.id === dialog.id) : undefined;
  const active = data?.subscriptions.filter((s) => s.status === 'active') || [];
  const monthly = data
    ? active.reduce((sum, sub) => sum + monthlyCost(sub, data.settings.rates), 0)
    : 0;
  const reminders = data
    ? active
        .filter((s) => attention(s, data.today).days <= s.remindDays)
        .sort((a, b) => relevantDate(a).localeCompare(relevantDate(b)))
    : [];
  const manualReminders = reminders.filter((s) => !s.autoRenew);
  const upcoming30 = data
    ? active.filter(
        (s) => attention(s, data.today).days <= 30 && attention(s, data.today).days >= 0,
      )
    : [];
  const filtered = useMemo(() => {
    if (!data) return [];
    const search = query.toLocaleLowerCase();
    return data.subscriptions
      .filter(
        (sub) =>
          (category === 'all' || sub.category === category) &&
          (status === 'all' || sub.status === status) &&
          (kind === 'once' ? sub.cycle === 'once' : sub.cycle !== 'once') &&
          (renew === 'all' || sub.autoRenew === (renew === 'auto')) &&
          (!search ||
            [sub.name, sub.provider, sub.resource, sub.note, ...sub.tags].some((value) =>
              value.toLocaleLowerCase().includes(search),
            )),
      )
      .sort((a, b) =>
        sort === 'name'
          ? a.name.localeCompare(b.name, 'zh-CN')
          : sort === 'cost'
            ? monthlyCost(b, data.settings.rates) - monthlyCost(a, data.settings.rates)
            : (relevantDate(a) || '9999').localeCompare(relevantDate(b) || '9999'),
      );
  }, [data, query, category, status, kind, renew, sort]);
  const firstOfMonth = data ? `${data.today.slice(0, 7)}-01` : '';
  const thisMonthBills = data
    ? calendarEvents(active, firstOfMonth, addDays(addMonths(firstOfMonth, 1), -1)).filter(
        (e) => e.type === 'payment',
      )
    : [];
  const planned = data
    ? thisMonthBills.reduce(
        (sum, e) => sum + e.amount * data.settings.rates[e.subscription.currency],
        0,
      )
    : 0;
  const demos = data?.subscriptions.filter((s) => s.isDemo).length || 0;
  const onlineWorkspace = data?.access.deployment === 'server';
  const categoryScope =
    data?.subscriptions.filter(
      (s) =>
        (kind === 'once' ? s.cycle === 'once' : s.cycle !== 'once') &&
        (status === 'all' || s.status === status),
    ) || [];

  if (!data)
    return (
      <div className="gate-page">
        <div className="gate-decoration" />
        <div className="gate-card">
          <div className="brand">
            <span className="brand-icon">
              <RotateCw size={24} />
            </span>
            <span>
              续期 <b>RENEW</b>
            </span>
          </div>
          {loadError ? (
            <>
              <h1>连接暂时中断</h1>
              <p>{loadError}</p>
              <button className="button primary" onClick={() => void refresh()}>
                <RefreshCw size={16} />
                重新连接
              </button>
            </>
          ) : access && !access.authenticated ? (
            <LoginForm access={access} onLogin={refresh} />
          ) : (
            <>
              <div className="loading-orbit">
                <RotateCw size={26} />
              </div>
              <h2>把订阅整理好，马上就来。</h2>
              <p>正在连接你的工作空间…</p>
            </>
          )}
        </div>
      </div>
    );

  return (
    <div className={`app-shell ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
      <aside className="sidebar" id="desktop-sidebar">
        <a href="#overview" className="brand" aria-label="续期总览">
          <span className="brand-icon">
            <RotateCw size={23} />
          </span>
          <span>
            续期 <b>RENEW</b>
          </span>
        </a>
        <div className="workspace-switch">
          <span className="workspace-avatar">R</span>
          <div>
            <b>{data.settings.workspaceName}</b>
            <small>个人订阅管理</small>
          </div>
          <ChevronDown size={14} />
        </div>
        <div className="nav-caption">工作空间</div>
        <nav className="desktop-nav" aria-label="主导航">
          {navigation.map((n) => (
            <button
              key={n.id}
              title={sidebarCollapsed ? n.label : undefined}
              aria-label={
                n.label === '总览'
                  ? '订阅总览'
                  : n.label === '订阅'
                    ? '全部订阅'
                    : n.label === '日历'
                      ? '付款日历'
                      : n.label === '分析'
                        ? '费用分析'
                        : '工作空间设置'
              }
              className={view === n.id ? 'active' : ''}
              onClick={() => setView(n.id)}
            >
              <n.icon size={19} strokeWidth={1.8} />
              <span>
                {n.label === '总览'
                  ? '订阅总览'
                  : n.label === '订阅'
                    ? '全部订阅'
                    : n.label === '日历'
                      ? '付款日历'
                      : n.label === '分析'
                        ? '费用分析'
                        : '工作空间设置'}
              </span>
              {n.id === 'subscriptions' && <em>{active.length}</em>}
            </button>
          ))}
        </nav>
        <div className="nav-caption resource-caption">资源分类</div>
        <nav className="resource-nav" aria-label="资源分类">
          {Object.entries(categories)
            .slice(0, 3)
            .map(([key, meta]) => {
              const Icon = key === 'server' ? Server : key === 'domain' ? Globe2 : Layers3;
              return (
                <button
                  key={key}
                  title={sidebarCollapsed ? meta.label : undefined}
                  aria-label={meta.label}
                  className={view === 'subscriptions' && category === key ? 'active' : ''}
                  onClick={() => {
                    setCategory(key as Category);
                    setKind('recurring');
                    setStatus('active');
                    setRenew('all');
                    setView('subscriptions');
                  }}
                >
                  <Icon size={17} />
                  <span>{meta.label}</span>
                  <small>{active.filter((s) => s.category === key).length}</small>
                </button>
              );
            })}
        </nav>
        <div className="sidebar-bottom">
          <div className="local-card">
            <span className="local-card-icon">
              <ShieldCheck size={20} />
            </span>
            <b>{onlineWorkspace ? '一个工作空间，随处管理。' : '你的数据，留在本机。'}</b>
            <p>
              每一份订阅和付款记录
              <br />
              {onlineWorkspace ? '都保存在你的服务器。' : '都保存在你自己的电脑。'}
            </p>
            <button onClick={() => setDialog({ type: 'access' })}>
              在手机上访问
              <ArrowUpRight size={14} />
            </button>
          </div>
          <button className="sidebar-footer" onClick={() => setView('settings')}>
            <span className="online-dot" />
            <span className="workspace-location">
              {onlineWorkspace ? '在线工作空间' : '本地工作空间'}
            </span>
            <span>v1.1</span>
          </button>
        </div>
      </aside>
      <div className="workspace-main">
        <header className="topbar">
          <div className="breadcrumb">
            <button
              className="icon-button sidebar-toggle"
              aria-label={sidebarCollapsed ? '展开侧栏' : '收起侧栏'}
              title={sidebarCollapsed ? '展开侧栏' : '收起侧栏'}
              aria-expanded={!sidebarCollapsed}
              aria-controls="desktop-sidebar"
              onClick={() => setSidebarCollapsed((value) => !value)}
            >
              {sidebarCollapsed ? <PanelLeftOpen size={19} /> : <PanelLeftClose size={19} />}
            </button>
            <span>工作空间</span>
            <ChevronRight size={12} />
            <b>{navigation.find((n) => n.id === view)?.label}</b>
          </div>
          <div className="topbar-tools">
            <div className="global-search">
              <Search size={15} />
              <input
                ref={searchRef}
                aria-label="搜索所有订阅"
                placeholder="搜索订阅、域名…"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  if (e.target.value) {
                    setCategory('all');
                    setStatus('all');
                    setView('subscriptions');
                  }
                }}
              />
              <kbd>⌘ K</kbd>
            </div>
            <button
              className="icon-button theme-button"
              aria-label={theme === 'light' ? '切换深色模式' : '切换浅色模式'}
              onClick={() => setTheme((t) => (t === 'light' ? 'dark' : 'light'))}
            >
              {theme === 'light' ? <Moon size={18} /> : <Sun size={18} />}
            </button>
            <button
              className="icon-button notification-button"
              aria-label={`查看到期提醒，${reminders.length} 项`}
              onClick={() => setDialog({ type: 'reminders' })}
            >
              <Bell size={18} />
              {reminders.length > 0 && <i />}
            </button>
            <button
              className="profile-avatar"
              aria-label="打开工作空间设置"
              onClick={() => setView('settings')}
            >
              R
            </button>
          </div>
        </header>
        <main className={`main-content view-${view}`}>
          <div className="page-heading">
            <div>
              <div className="page-eyebrow">
                {view === 'overview'
                  ? new Intl.DateTimeFormat('zh-CN', {
                      timeZone: 'UTC',
                      month: 'long',
                      day: 'numeric',
                      weekday: 'long',
                    }).format(new Date(`${data.today}T00:00:00Z`))
                  : view === 'subscriptions'
                    ? 'EVERY SUBSCRIPTION, IN ONE PLACE'
                    : view === 'calendar'
                      ? 'PLAN AHEAD'
                      : view === 'analytics'
                        ? 'UNDERSTAND YOUR SPENDING'
                        : 'MAKE IT YOURS'}
                <span className="tiny-dot" />
                {view === 'overview' ? '很高兴见到你' : '续期 RENEW'}
              </div>
              <h1>
                {view === 'overview' ? (
                  <>
                    每一份订阅，<span>心中有数。</span>
                  </>
                ) : (
                  navigation.find((n) => n.id === view)?.label
                )}
              </h1>
              <p>
                {view === 'overview'
                  ? '管理你的服务器、域名与订阅，让下一次续费从容一点。'
                  : view === 'subscriptions'
                    ? '管理你的所有订阅服务，每一个重要日期都清晰可见。'
                    : view === 'calendar'
                      ? '付款与到期，都在你的日程里。'
                      : view === 'analytics'
                        ? '看清每一笔开支，把预算用在值得的地方。'
                        : '按照你的习惯，让续期更顺手。'}
              </p>
            </div>
            <div className="page-heading-actions">
              {view !== 'settings' && (
                <button
                  className="button secondary export-header"
                  onClick={() => {
                    window.location.href = '/api/export/json';
                  }}
                >
                  <Download size={16} />
                  导出
                </button>
              )}
              {view !== 'settings' && (
                <button
                  className="button primary add-button"
                  aria-label="添加订阅"
                  onClick={() => setDialog({ type: 'create' })}
                >
                  <Plus size={18} />
                  <span>添加订阅</span>
                </button>
              )}
            </div>
          </div>
          {loadError && (
            <div className="connection-warning" role="alert">
              <AlertCircle size={16} />
              {loadError}，当前展示上次同步的数据。
              <button onClick={() => void refresh()}>重试</button>
            </div>
          )}
          {view === 'overview' && (
            <>
              <div className="stats-grid">
                <Metric
                  icon={<Wallet size={18} />}
                  label="月均支出"
                  value={money(monthly)}
                  note="按订阅周期折算"
                  color="mint"
                />
                <Metric
                  icon={<TrendingUp size={18} />}
                  label="年度预计"
                  value={money(monthly * 12)}
                  note="仅统计启用的周期订阅"
                  color="blue"
                />
                <Metric
                  icon={<CalendarDays size={18} />}
                  label="30 天内待续费"
                  value={String(upcoming30.length)}
                  suffix="份"
                  note={
                    manualReminders.length
                      ? `${manualReminders.length} 份手动续费需要留意`
                      : '提前安排，安心使用'
                  }
                  color="peach"
                />
                <Metric
                  icon={<Layers3 size={18} />}
                  label="正在使用"
                  value={String(active.length)}
                  suffix="份"
                  note={`${active.filter((s) => s.category === 'server').length} 台服务器 · ${active.filter((s) => s.category === 'domain').length} 个域名`}
                  color="lavender"
                />
              </div>
              {manualReminders.length > 0 && (
                <button
                  className="attention-banner"
                  onClick={() => setDialog({ type: 'reminders' })}
                >
                  <span className="attention-icon">
                    <Bell size={18} />
                  </span>
                  <span>
                    <b>{manualReminders.length} 份订阅需要手动续费</b>
                    <small>
                      {manualReminders
                        .map((s) => s.name)
                        .slice(0, 3)
                        .join('、')}{' '}
                      · 别让重要服务中断
                    </small>
                  </span>
                  <span className="banner-link">
                    查看提醒
                    <ArrowRight size={15} />
                  </span>
                </button>
              )}
              <div className="overview-grid">
                <section className="overview-subscriptions">
                  <div className="section-heading">
                    <div>
                      <h2>
                        我的订阅 <span>{active.length}</span>
                      </h2>
                      <p>日期、费用与状态，一眼掌握</p>
                    </div>
                    <button
                      className="text-button"
                      onClick={() => {
                        setCategory('all');
                        setView('subscriptions');
                      }}
                    >
                      查看全部
                      <ArrowUpRight size={14} />
                    </button>
                  </div>
                  {active.length ? (
                    <div className="subscription-grid">
                      {[...active]
                        .sort((a, b) => relevantDate(a).localeCompare(relevantDate(b)))
                        .slice(0, 6)
                        .map((sub) => (
                          <SubscriptionCard
                            sub={sub}
                            today={data.today}
                            key={sub.id}
                            onOpen={open}
                            onPay={pay}
                          />
                        ))}
                    </div>
                  ) : (
                    <section className="panel">
                      <EmptyState onAdd={() => setDialog({ type: 'create' })} />
                    </section>
                  )}
                </section>
                <aside className="overview-rail">
                  <section className="panel cost-panel">
                    <div className="panel-heading">
                      <h3>支出分布</h3>
                      <button
                        className="icon-button"
                        aria-label="查看费用分析"
                        onClick={() => setView('analytics')}
                      >
                        <ArrowUpRight size={16} />
                      </button>
                    </div>
                    <CostBreakdown data={data} compact />
                    <div className="panel-bottom-note">外币按手动汇率估算</div>
                  </section>
                  <section className="panel upcoming-panel">
                    <div className="panel-heading">
                      <h3>近期日程</h3>
                      <button
                        className="icon-button"
                        aria-label="打开付款日历"
                        onClick={() => setView('calendar')}
                      >
                        <ArrowUpRight size={16} />
                      </button>
                    </div>
                    <UpcomingList
                      subscriptions={active}
                      today={data.today}
                      onOpen={open}
                      limit={4}
                    />
                  </section>
                  <div className="month-summary">
                    <CalendarDays size={19} />
                    <div>
                      <small>{Number(data.today.slice(5, 7))} 月计划付款</small>
                      <b>
                        {money(planned)} <span>/ {thisMonthBills.length} 笔</span>
                      </b>
                    </div>
                    <button
                      className="icon-button"
                      aria-label="查看本月付款"
                      onClick={() => setView('calendar')}
                    >
                      <ArrowUpRight size={16} />
                    </button>
                  </div>
                </aside>
              </div>
            </>
          )}
          {view === 'subscriptions' && (
            <>
              <div className="subscription-controls">
                <div className="segmented subscription-segment">
                  <button
                    className={kind === 'recurring' ? 'active' : ''}
                    onClick={() => {
                      setKind('recurring');
                      setStatus('active');
                    }}
                  >
                    订阅{' '}
                    <span>
                      {
                        data.subscriptions.filter(
                          (s) => s.cycle !== 'once' && s.status === 'active',
                        ).length
                      }
                    </span>
                  </button>
                  <button
                    className={kind === 'once' ? 'active' : ''}
                    onClick={() => {
                      setKind('once');
                      setStatus('all');
                      setRenew('all');
                    }}
                  >
                    买断 <span>{data.subscriptions.filter((s) => s.cycle === 'once').length}</span>
                  </button>
                </div>
                <button
                  className={`icon-button filters-button ${showFilters ? 'active' : ''}`}
                  aria-label="筛选订阅"
                  aria-expanded={showFilters}
                  onClick={() => setShowFilters((v) => !v)}
                >
                  <SlidersHorizontal size={21} />
                </button>
                <div className="controls-spacer" />
                <div className="search-input">
                  <Search size={16} />
                  <input
                    aria-label="搜索当前订阅"
                    placeholder="搜索名称、服务商、标签"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                  />
                  {query && (
                    <button
                      className="icon-button"
                      aria-label="清空搜索"
                      onClick={() => setQuery('')}
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
                <select
                  className="sort-select"
                  aria-label="订阅排序"
                  value={sort}
                  onChange={(e) => setSort(e.target.value)}
                >
                  <option value="date">按临近日期</option>
                  <option value="cost">按月均费用</option>
                  <option value="name">按订阅名称</option>
                </select>
                <div className="layout-toggle">
                  <button
                    aria-label="卡片视图"
                    className={layout === 'cards' ? 'active' : ''}
                    onClick={() => setLayout('cards')}
                  >
                    <LayoutGrid size={17} />
                  </button>
                  <button
                    aria-label="列表视图"
                    className={layout === 'table' ? 'active' : ''}
                    onClick={() => setLayout('table')}
                  >
                    <Rows3 size={17} />
                  </button>
                </div>
              </div>
              {showFilters && (
                <div className="filters-panel">
                  <label>
                    状态
                    <select
                      value={status}
                      onChange={(e) => setStatus(e.target.value as typeof status)}
                    >
                      <option value="active">正在使用</option>
                      <option value="paused">已停用</option>
                      <option value="all">全部状态</option>
                    </select>
                  </label>
                  <label>
                    续费方式
                    <select
                      value={renew}
                      onChange={(e) => setRenew(e.target.value as typeof renew)}
                    >
                      <option value="all">全部方式</option>
                      <option value="manual">手动续费</option>
                      <option value="auto">自动续费</option>
                    </select>
                  </label>
                  <button
                    className="text-button"
                    onClick={() => {
                      setStatus('active');
                      setRenew('all');
                      setQuery('');
                      setCategory('all');
                    }}
                  >
                    重置筛选
                  </button>
                </div>
              )}
              <div className="category-tabs" role="group" aria-label="订阅分类">
                <button
                  className={category === 'all' ? 'active' : ''}
                  onClick={() => setCategory('all')}
                >
                  全部 <small>{categoryScope.length}</small>
                </button>
                {Object.entries(categories).map(([key, meta]) => (
                  <button
                    className={category === key ? 'active' : ''}
                    key={key}
                    onClick={() => setCategory(key as Category)}
                  >
                    {meta.label}
                    <small>{categoryScope.filter((s) => s.category === key).length}</small>
                  </button>
                ))}
                <span className="results-count">{filtered.length} 份订阅</span>
              </div>
              {filtered.length ? (
                <>
                  <div
                    className={`subscription-grid full-grid ${layout === 'table' ? 'mobile-only-grid' : ''}`}
                  >
                    {filtered.map((sub) => (
                      <SubscriptionCard
                        sub={sub}
                        today={data.today}
                        key={sub.id}
                        onOpen={open}
                        onPay={pay}
                      />
                    ))}
                  </div>
                  {layout === 'table' && (
                    <section className="panel subscription-table">
                      <div className="subscription-table-row table-head">
                        <span>订阅 / 资源</span>
                        <span>金额 / 周期</span>
                        <span>开始日期</span>
                        <span>下次付款</span>
                        <span>资源到期</span>
                        <span>续费方式</span>
                        <span />
                      </div>
                      {filtered.map((sub) => (
                        <div className="subscription-table-row" key={sub.id}>
                          <button className="table-service" onClick={() => open(sub)}>
                            <ServiceIcon sub={sub} small />
                            <span>
                              <b>{sub.name}</b>
                              <small>{sub.resource || sub.provider}</small>
                            </span>
                          </button>
                          <span className="table-price">
                            <b>{money(sub.price, sub.currency)}</b>
                            <small>/{cycles[sub.cycle].short}</small>
                          </span>
                          <span>{fullDate(sub.startDate)}</span>
                          <span className="table-date">
                            <b>{fullDate(sub.nextPayment)}</b>
                            <StatusBadge sub={sub} today={data.today} />
                          </span>
                          <span>{fullDate(sub.expiryDate)}</span>
                          <span className={`renew-mode ${sub.autoRenew ? 'auto' : ''}`}>
                            <span />
                            {sub.autoRenew ? '自动' : '手动'}
                          </span>
                          <button
                            className="icon-button"
                            aria-label={`查看 ${sub.name}`}
                            onClick={() => open(sub)}
                          >
                            <ArrowUpRight size={17} />
                          </button>
                        </div>
                      ))}
                    </section>
                  )}
                </>
              ) : (
                <section className="panel">
                  <EmptyState
                    title={
                      query || category !== 'all' || renew !== 'all' || status !== 'active'
                        ? '没有找到匹配的订阅'
                        : kind === 'once'
                          ? '把一次性购买也记下来'
                          : '添加你的第一份订阅'
                    }
                    text={
                      query
                        ? '试试其他关键词，或调整筛选条件。'
                        : '服务器、域名、软件，每一种都可以妥善管理。'
                    }
                    onAdd={() => setDialog({ type: 'create' })}
                  />
                </section>
              )}
            </>
          )}
          {view === 'calendar' && <CalendarPage data={data} onOpen={open} />}
          {view === 'analytics' && <AnalyticsPage data={data} />}
          {view === 'settings' && (
            <SettingsPage
              data={data}
              mutate={mutate}
              onAccess={() => setDialog({ type: 'access' })}
              notify={notify}
              confirm={confirm}
            />
          )}
          <footer className="workspace-footer">
            <span>
              <span className="online-dot" />
              {onlineWorkspace ? '服务器统一保存' : '数据保存在本机'}
              <span className="footer-separator">·</span>
              {demos ? (
                <button onClick={() => setView('settings')}>{demos} 份示例数据</button>
              ) : (
                '安心管理你的每一份订阅'
              )}
            </span>
            <button
              onClick={() => void refresh()}
              disabled={syncing}
              className={loadError ? 'sync-error' : ''}
              title={`上次同步 ${new Date(lastSync).toLocaleTimeString('zh-CN')}`}
            >
              <RefreshCw size={12} className={syncing ? 'sync-spinning' : ''} />
              {syncing ? '同步中…' : loadError ? '同步中断 · 重试' : '已同步 · 点击刷新'}
            </button>
          </footer>
        </main>
      </div>
      <nav className="mobile-nav" aria-label="手机主导航">
        {navigation.map((n) => (
          <button
            className={view === n.id ? 'active' : ''}
            key={n.id}
            onClick={() => setView(n.id)}
          >
            <n.icon size={24} strokeWidth={1.9} />
            <span>{n.label === '总览' ? '概览' : n.label}</span>
          </button>
        ))}
      </nav>
      {dialog?.type === 'create' && (
        <SubscriptionForm today={data.today} onClose={() => setDialog(null)} mutate={mutate} />
      )}
      {dialog?.type === 'edit' && selected && (
        <SubscriptionForm
          sub={selected}
          today={data.today}
          onClose={() => setDialog(null)}
          mutate={mutate}
        />
      )}
      {dialog?.type === 'pay' && selected && (
        <PaymentForm
          sub={selected}
          today={data.today}
          onClose={() => setDialog(null)}
          mutate={mutate}
        />
      )}
      {dialog?.type === 'detail' && selected && (
        <DetailModal
          sub={selected}
          payments={data.payments}
          today={data.today}
          onClose={() => setDialog(null)}
          onEdit={() => setDialog({ type: 'edit', id: selected.id })}
          onPay={() => setDialog({ type: 'pay', id: selected.id })}
          mutate={mutate}
          confirm={confirm}
        />
      )}
      {dialog?.type === 'access' && (
        <AccessModal
          access={data.access}
          onClose={() => setDialog(null)}
          mutate={mutate}
          notify={notify}
        />
      )}
      {dialog?.type === 'reminders' && (
        <Modal title="值得留意的续费" eyebrow="A LITTLE REMINDER" onClose={() => setDialog(null)}>
          <p className="reminder-intro">
            根据每份订阅的提前提醒天数显示。逾期项目会持续保留，直到你记录付款或更新日期。
          </p>
          {reminders.length ? (
            reminders.map((sub) => (
              <button key={sub.id} className="reminder-row" onClick={() => open(sub)}>
                <ServiceIcon sub={sub} small />
                <span>
                  <b>{sub.name}</b>
                  <small>
                    付款 {shortDate(sub.nextPayment)}
                    {sub.expiryDate && ` · 到期 ${shortDate(sub.expiryDate)}`}
                  </small>
                </span>
                <span>
                  <StatusBadge sub={sub} today={data.today} />
                  <small>{sub.autoRenew ? '自动续费' : '手动续费'}</small>
                </span>
                <ArrowUpRight size={14} />
              </button>
            ))
          ) : (
            <EmptyState title="目前一切妥当" text="临近付款或到期时，会在这里提醒你。" />
          )}
          <div className="info-callout">
            <InfoIcon />
            <span>关闭应用后可使用日历提醒：在设置中导出日历，导入手机日历即可。</span>
          </div>
        </Modal>
      )}
      {confirmation && <ConfirmModal {...confirmation} onClose={() => setConfirmation(null)} />}
      {toast && (
        <div className="toast" role="status">
          <Check size={16} />
          <span>{toast}</span>
          <button aria-label="关闭提示" onClick={() => setToast('')}>
            <X size={14} />
          </button>
        </div>
      )}
    </div>
  );
}

function InfoIcon() {
  return <CircleHelp size={16} />;
}
function Metric({
  icon,
  label,
  value,
  suffix,
  note,
  color,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  suffix?: string;
  note: string;
  color: string;
}) {
  return (
    <div className={`metric-card ${color}`}>
      <div className="metric-label">
        <span>{label}</span>
        <i>{icon}</i>
      </div>
      <div className="metric-value">
        {value}
        <small>{suffix}</small>
      </div>
      <p>{note}</p>
    </div>
  );
}

function LoginForm({ access, onLogin }: { access: AccessInfo; onLogin: () => Promise<void> }) {
  const [password, setPassword] = useState(''),
    [error, setError] = useState(''),
    [busy, setBusy] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const online = access.deployment === 'server';
  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      await api('/login', 'POST', { password });
      await onLogin();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <>
      <span className="login-icon">
        <Smartphone size={28} />
      </span>
      <h1>你的订阅，随身管理。</h1>
      <p>
        {access.passwordEnabled
          ? online
            ? '输入工作空间访问密码。手机和电脑共享同一份订阅与付款记录。'
            : '输入电脑上设置的访问密码，继续管理同一个工作空间。'
          : online
            ? '工作空间尚未设置访问密码，请联系管理员。'
            : '请先在电脑打开续期，在「设置 → 手机访问」中创建访问密码。'}
      </p>
      {access.passwordEnabled && (
        <form onSubmit={submit}>
          <label>
            访问密码
            <div className="input-with-icon">
              <KeyRound size={17} />
              <input
                type={showPassword ? 'text' : 'password'}
                name="password"
                autoComplete="current-password"
                required
                autoFocus
                placeholder="输入你的访问密码"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <button
                type="button"
                className="icon-button"
                aria-label={showPassword ? '隐藏密码' : '显示密码'}
                onClick={() => setShowPassword((value) => !value)}
              >
                {showPassword ? <EyeOff size={17} /> : <Eye size={17} />}
              </button>
            </div>
          </label>
          {error && (
            <p className="form-error" role="alert">
              {error}
            </p>
          )}
          <button className="button primary" disabled={busy} type="submit">
            {busy ? '正在连接…' : '进入工作空间'}
            <ArrowRight size={16} />
          </button>
        </form>
      )}
      <small className="login-note">
        <ShieldCheck size={13} />
        {online
          ? 'HTTPS 加密访问 · 无需同一 Wi-Fi · 登录保持 30 天'
          : '数据留在你的电脑，手机与电脑共用。'}
      </small>
    </>
  );
}
